'use client'

import { useVoiceConfigStore } from '@/stores/voice-config-store'
import { createStore, type StoreApi } from 'zustand/vanilla'
import { useStore } from 'zustand'
import { createContext, useContext, useRef, type ReactNode } from 'react'
import type { ApiUser } from '@/api'

export const STORAGE_KEY = 'obzoo-auth'

interface PersistedAuth {
  user: ApiUser
  token: string
  expiresAt: number
}

function persistAuth(data: PersistedAuth | null) {
  if (typeof window === 'undefined') return
  if (data === null) {
    localStorage.removeItem(STORAGE_KEY)
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }
}

export interface AuthState {
  currentUser: ApiUser | null
  token: string | null
  expiresAt: number | null

  setAuth: (user: ApiUser, token: string, expiresAt: number) => void
  clearAuth: () => void
  initFromStorage: () => boolean
}

function readStorage(): Pick<AuthState, 'currentUser' | 'token' | 'expiresAt'> {
  if (typeof window === 'undefined') return { currentUser: null, token: null, expiresAt: null }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { currentUser: null, token: null, expiresAt: null }
    const data: PersistedAuth = JSON.parse(raw)
    if (Date.now() >= data.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return { currentUser: null, token: null, expiresAt: null }
    }
    return { currentUser: data.user, token: data.token, expiresAt: data.expiresAt }
  } catch {
    localStorage.removeItem(STORAGE_KEY)
    return { currentUser: null, token: null, expiresAt: null }
  }
}

export const createAuthStore = () =>
  createStore<AuthState>((set) => ({
    // Synchronously hydrate from localStorage so the route guard has token on first render
    ...readStorage(),

    setAuth: (user, token, expiresAt) => {
      persistAuth({ user, token, expiresAt })
      // A different account may now be signed in: never show the previous one's team voice config.
      useVoiceConfigStore.getState().clear()
      set({ currentUser: user, token, expiresAt })
    },

    clearAuth: () => {
      persistAuth(null)
      useVoiceConfigStore.getState().clear()
      set({ currentUser: null, token: null, expiresAt: null })
    },

    // Keep for explicit re-reads if needed
    initFromStorage: () => {
      const state = readStorage()
      if (!state.token) return false
      set(state)
      return true
    },
  }))

// Context
const AuthStoreContext = createContext<StoreApi<AuthState> | null>(null)

// Provider
export function AuthStoreProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<StoreApi<AuthState>>(null)
  if (!storeRef.current) {
    storeRef.current = createAuthStore()
  }
  return (
    <AuthStoreContext value={storeRef.current}>
      {children}
    </AuthStoreContext>
  )
}

// Hook
export function useAuthStore<T>(selector: (state: AuthState) => T): T {
  const store = useContext(AuthStoreContext)
  if (!store) throw new Error('Missing AuthStoreProvider')
  return useStore(store, selector)
}

// Raw store API — for accessing getState() outside React render (e.g., in callbacks)
export function useAuthStoreApi(): StoreApi<AuthState> {
  const store = useContext(AuthStoreContext)
  if (!store) throw new Error('Missing AuthStoreProvider')
  return store
}
